<script setup>
import { reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ColorPicker from 'dashboard/components-next/colorpicker/ColorPicker.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import GreetingsEditor from 'shared/components/GreetingsEditor.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const state = reactive({
  inboxName: '',
  websiteUrl: '',
  widgetColor: '#6E56CF',
  welcomeTitle: '',
  welcomeTagline: '',
  greetingEnabled: false,
  greetingMessage: '',
});

const uiFlags = useMapGetter('inboxes/getUIFlags');

const validationRules = {
  inboxName: { required },
  websiteUrl: { required },
};

const v$ = useVuelidate(validationRules, state);
const isSubmitDisabled = computed(() => v$.value.$invalid);

const formErrors = computed(() => ({
  inboxName: v$.value.inboxName.$error
    ? t('INBOX_MGMT.ADD.WEBSITE_NAME.ERROR')
    : '',
  websiteUrl: v$.value.websiteUrl.$error
    ? t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_DOMAIN.ERROR')
    : '',
}));

const createChannel = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  try {
    const website = await store.dispatch('inboxes/createWebsiteChannel', {
      name: state.inboxName.trim(),
      greeting_enabled: state.greetingEnabled,
      greeting_message: state.greetingMessage,
      channel: {
        type: 'web_widget',
        website_url: state.websiteUrl,
        widget_color: state.widgetColor,
        welcome_title: state.welcomeTitle,
        welcome_tagline: state.welcomeTagline,
      },
    });
    router.replace({
      name: 'settings_inboxes_add_agents',
      params: {
        page: 'new',
        inbox_id: website.id,
      },
    });
  } catch (error) {
    useAlert(
      error.message || t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.API.ERROR_MESSAGE')
    );
  }
};
</script>

<template>
  <section
    class="w-full max-w-3xl px-4 py-6 mx-auto sm:px-6 sm:py-8"
    aria-labelledby="website-channel-title"
  >
    <div class="max-w-2xl">
      <p class="mb-2 text-xs font-semibold text-n-violet-11">
        {{ t('INBOX_MGMT.CREATE_FLOW.BUILDER.STEP_LABEL', { step: 2 }) }}
      </p>
      <h2
        id="website-channel-title"
        class="text-2xl font-semibold tracking-tight text-n-slate-12"
      >
        {{ t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.TITLE') }}
      </h2>
      <p class="mt-2 mb-0 text-sm leading-6 text-n-slate-11">
        {{ t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.DESC') }}
      </p>
    </div>

    <form class="flex flex-col gap-5 mt-7" @submit.prevent="createChannel">
      <fieldset
        class="p-5 border rounded-xl border-n-weak bg-n-solid-1 min-w-0"
      >
        <legend class="text-sm font-semibold text-n-slate-12">
          {{ t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.SECTIONS.DETAILS.TITLE') }}
        </legend>
        <p class="mt-1 mb-4 text-xs text-n-slate-11">
          {{ t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.SECTIONS.DETAILS.DESC') }}
        </p>
        <div class="flex flex-col gap-4">
          <Input
            v-model="state.inboxName"
            :label="t('INBOX_MGMT.ADD.WEBSITE_NAME.LABEL')"
            :placeholder="t('INBOX_MGMT.ADD.WEBSITE_NAME.PLACEHOLDER')"
            :message="formErrors.inboxName"
            :message-type="formErrors.inboxName ? 'error' : 'info'"
            @blur="v$.inboxName.$touch"
          />
          <Input
            v-model="state.websiteUrl"
            :label="t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_DOMAIN.LABEL')"
            :placeholder="
              t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_DOMAIN.PLACEHOLDER')
            "
            :message="formErrors.websiteUrl"
            :message-type="formErrors.websiteUrl ? 'error' : 'info'"
            @blur="v$.websiteUrl.$touch"
          />
        </div>
      </fieldset>

      <fieldset
        class="p-5 border rounded-xl border-n-weak bg-n-solid-1 min-w-0"
      >
        <legend class="text-sm font-semibold text-n-slate-12">
          {{ t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.SECTIONS.APPEARANCE.TITLE') }}
        </legend>
        <p class="mt-1 mb-4 text-xs text-n-slate-11">
          {{ t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.SECTIONS.APPEARANCE.DESC') }}
        </p>
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-1">
            <span class="mb-0.5 text-heading-3 text-n-slate-12">
              {{ t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.WIDGET_COLOR.LABEL') }}
            </span>
            <ColorPicker v-model="state.widgetColor" />
          </div>
          <Input
            v-model="state.welcomeTitle"
            :label="
              t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TITLE.LABEL')
            "
            :placeholder="
              t(
                'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TITLE.PLACEHOLDER'
              )
            "
          />
          <Editor
            v-model="state.welcomeTagline"
            :label="
              t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TAGLINE.LABEL')
            "
            :placeholder="
              t(
                'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TAGLINE.PLACEHOLDER'
              )
            "
            :max-length="255"
            channel-type="Context::InboxSettings"
          />
        </div>
      </fieldset>

      <fieldset
        class="p-5 border rounded-xl border-n-weak bg-n-solid-1 min-w-0"
      >
        <legend class="sr-only">
          {{
            t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_TOGGLE.LABEL')
          }}
        </legend>
        <div class="flex items-center justify-between gap-4">
          <span
            id="website-greeting-label"
            class="text-sm font-semibold text-n-slate-12"
          >
            {{
              t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_TOGGLE.LABEL')
            }}
          </span>
          <Switch
            v-model="state.greetingEnabled"
            aria-labelledby="website-greeting-label"
          />
        </div>
        <p class="mt-1 mb-0 text-xs text-n-slate-11">
          {{
            t(
              'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_TOGGLE.HELP_TEXT'
            )
          }}
        </p>
        <GreetingsEditor
          v-if="state.greetingEnabled"
          v-model="state.greetingMessage"
          class="mt-4"
          :label="
            t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_MESSAGE.LABEL')
          "
          :placeholder="
            t(
              'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_MESSAGE.PLACEHOLDER'
            )
          "
          richtext
        />
      </fieldset>

      <div class="flex justify-end">
        <NextButton
          type="submit"
          :is-loading="uiFlags.isCreating"
          :disabled="isSubmitDisabled"
          solid
          blue
          :label="t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.SUBMIT_BUTTON')"
        />
      </div>
    </form>
  </section>
</template>
